import { getLocaleArray } from '@utils/CommonOptionType';
import { isStringIncludes } from '@utils/isStringIncludes';
import { assertIsDefined } from '@utils/assertIsDefined';
import i18next from 'i18next';
import { makeAutoObservable, computed } from 'mobx';

import { ConfigurationApi } from '@api/ConfigurationApi';
import { Locale } from 'chaika-enums';
import { allTranslations, getTranslation } from 'chaika-translation-data';
import { TranslationDataViewModel } from 'chaika-types';

export interface TranslationTableData {
    translationKey: string;
    [Locale.Ru]: string;
    [Locale.En]: string;
    [Locale.Am]: string;
    [Locale.Pt]: string;
    [Locale.Ge]: string;
}

const tn = 'translation';

class TranslationDataService {
    private isLoaded = false;
    private translationData: TranslationDataViewModel[] = [];
    private query: string = '';

    constructor() {
        makeAutoObservable(this);
    }

    @computed
    public get translationsForUpdate(): TranslationDataViewModel[] {
        return this.translationData.filter(
            // @ts-expect-error
            ({ translationKey, locale, value }) => getTranslation(translationKey, locale) !== value,
        );
    }

    @computed
    public get translationsForRemove(): TranslationDataViewModel[] {
        return this.translationData.filter(
            // @ts-expect-error
            ({ translationKey, locale, value }) => getTranslation(translationKey, locale) === value,
        );
    }

    @computed
    public get allTranslations(): TranslationTableData[] {
        const allTranslations = this.getAllTranslations();

        return this.query !== ''
            ? allTranslations.filter(
                  ({ translationKey, ...params }) =>
                      isStringIncludes(translationKey, this.query) ||
                      getLocaleArray().some(locale => isStringIncludes(params[locale], this.query)),
              )
            : allTranslations;
    }

    public updateQuey(value?: string): void {
        this.query = value ?? '';
    }

    public async load(): Promise<void> {
        if (!this.isLoaded) {
            this.isLoaded = true;
            this.translationData = await ConfigurationApi.getTranslationData();
            this.translationData.forEach(({ translationKey, locale, value }) => {
                i18next.addResource(locale, tn, translationKey, value);
            });
        }
    }

    public async update(translationKey: string, params: Partial<TranslationTableData>): Promise<void> {
        await Promise.all(
            getLocaleArray()
                .filter(
                    locale => !!params[locale] && i18next.getResource(locale, tn, translationKey) !== params[locale],
                )
                .map(locale => {
                    const value = params[locale];
                    assertIsDefined(value, `Locale ${locale} is kept above only with a translation value`);
                    i18next.addResource(locale, tn, translationKey, value);
                    this.translationData = this.translationData.map(data =>
                        data.translationKey === translationKey && data.locale === locale ? { ...data, value } : data,
                    );
                    return ConfigurationApi.updateTranslationData(translationKey, { locale, value });
                }),
        );
    }

    private getAllTranslations(): TranslationTableData[] {
        const translationsData: TranslationTableData[] = [];
        Object.keys(allTranslations).forEach(translationPath => {
            Object.keys(allTranslations[translationPath as keyof typeof allTranslations]).forEach(translationKey => {
                const {
                    translationKey: key,
                    locale,
                    value,
                } = this.translationData.find(data => data.translationKey === translationKey) ?? {};

                // @ts-expect-error
                const params: { [key: string]: string } = !!key ? { [locale]: value } : {};
                translationsData.push({
                    translationKey,
                    //@ts-expect-error
                    ...allTranslations[translationPath][translationKey],
                    ...params,
                });
            });
        });

        return translationsData;
    }
}

export const translationDataService = new TranslationDataService();
